import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { OtCategoryFields, otCategoryPayload } from "@/components/OtCategoryFields";
import { Timer, Plus, Trash, Info, PaperPlaneTilt } from "@phosphor-icons/react";

const STATUS_BADGE = {
  pending: "bg-warning/10 text-warning border-warning/20",
  approved: "bg-success/10 text-success border-success/20",
  rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

const OT_TYPES = ["Regular Overtime", "Restday Duty", "Legal Holiday", "Special Holiday"];

const emptyRow = () => ({ date: "", ot_type: "", category: "None", direct_call: "", account_name: "", contact_person: "", reason: "", time_from: "", time_to: "", total_hours: "", approver_id: "", remarks: "" });

function calcHours(from, to) {
  if (!from || !to) return "";
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  if ([fh, fm, th, tm].some((n) => Number.isNaN(n))) return "";
  let mins = (th * 60 + tm) - (fh * 60 + fm);
  if (mins < 0) mins += 24 * 60;
  return String(Math.round((mins / 60) * 100) / 100);
}

export default function OvertimeFile() {
  const { user } = useAuth();
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [cutOff, setCutOff] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [approvers, setApprovers] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const loadMine = useCallback(async () => {
    try {
      const { data } = await api.get("/overtime/mine");
      setMine(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    setDepartment(user?.department || "");
    setPosition(user?.position || "");
  }, [user]);

  useEffect(() => {
    api.get("/overtime/approvers").then(({ data }) => setApprovers(data)).catch(() => {});
    loadMine();
  }, [loadMine]);

  const updateRow = (i, patch) => setRows((rs) => rs.map((r, idx) => {
    if (idx !== i) return r;
    const next = { ...r, ...patch };
    if (patch.time_from !== undefined || patch.time_to !== undefined) {
      const auto = calcHours(next.time_from, next.time_to);
      if (auto !== "") next.total_hours = auto;
    }
    return next;
  }));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs);

  const submit = async () => {
    const valid = rows.filter((r) => r.date && r.reason && r.approver_id);
    if (!valid.length) {
      toast.error("Add at least one complete row (date, reason, and approver).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/overtime", {
        department, position, cut_off: cutOff,
        rows: valid.map((r) => ({
          date: r.date, ot_type: r.ot_type, ...otCategoryPayload(r), reason: r.reason, time_from: r.time_from, time_to: r.time_to,
          total_hours: parseFloat(r.total_hours) || 0, approver_id: r.approver_id, remarks: r.remarks,
        })),
      });
      toast.success("Overtime filed — sent to your approver for review");
      setRows([emptyRow()]);
      setCutOff("");
      loadMine();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="overtime-file-page">
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
          <Timer weight="duotone" className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Employee</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">File Overtime</h1>
          <p className="text-muted-foreground mt-2 text-sm">Fill in your overtime details and submit for approval.</p>
        </div>
      </div>

      <Card className="p-6 border-border shadow-none space-y-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={user?.name || ""} disabled data-testid="ot-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Input value={department} onChange={(e) => setDepartment(e.target.value)} data-testid="ot-department" />
          </div>
          <div className="space-y-1.5">
            <Label>Position</Label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)} data-testid="ot-position" />
          </div>
          <div className="space-y-1.5">
            <Label>Date filed</Label>
            <Input value={today} disabled data-testid="ot-date-filed" />
          </div>
          <div className="space-y-1.5">
            <Label>Cut off</Label>
            <Input value={cutOff} onChange={(e) => setCutOff(e.target.value)} placeholder="e.g. 1st–15th of the month" data-testid="ot-cutoff" />
          </div>
        </div>

        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3.5 flex items-start gap-2.5 text-sm" data-testid="ot-note">
          <Info weight="duotone" className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <span>Please submit the overtime form to your supervisor for approval prior to working any overtime.</span>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="min-w-[140px]">Date</TableHead>
                <TableHead className="min-w-[170px]">Type</TableHead>
                <TableHead className="min-w-[200px]">Category</TableHead>
                <TableHead className="min-w-[200px]">Reason (Work to be Done)</TableHead>
                <TableHead className="min-w-[110px]">From</TableHead>
                <TableHead className="min-w-[110px]">To</TableHead>
                <TableHead className="min-w-[90px]">Total Hrs</TableHead>
                <TableHead className="min-w-[180px]">Manager/Supervisor</TableHead>
                <TableHead className="min-w-[160px]">Remarks</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`ot-row-${i}`}>
                  <TableCell><Input type="date" value={r.date} onChange={(e) => updateRow(i, { date: e.target.value })} data-testid={`ot-row-date-${i}`} /></TableCell>
                  <TableCell>
                    <Select value={r.ot_type} onValueChange={(v) => updateRow(i, { ot_type: v })}>
                      <SelectTrigger data-testid={`ot-row-type-${i}`}><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {OT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="align-top"><OtCategoryFields row={r} onChange={(p) => updateRow(i, p)} testPrefix={`ot-row-${i}`} /></TableCell>
                  <TableCell><Input value={r.reason} onChange={(e) => updateRow(i, { reason: e.target.value })} placeholder="Work to be done" data-testid={`ot-row-reason-${i}`} /></TableCell>
                  <TableCell><Input type="time" value={r.time_from} onChange={(e) => updateRow(i, { time_from: e.target.value })} data-testid={`ot-row-from-${i}`} /></TableCell>
                  <TableCell><Input type="time" value={r.time_to} onChange={(e) => updateRow(i, { time_to: e.target.value })} data-testid={`ot-row-to-${i}`} /></TableCell>
                  <TableCell><Input type="number" step="0.25" value={r.total_hours} onChange={(e) => updateRow(i, { total_hours: e.target.value })} data-testid={`ot-row-hours-${i}`} /></TableCell>
                  <TableCell>
                    <Select value={r.approver_id} onValueChange={(v) => updateRow(i, { approver_id: v })}>
                      <SelectTrigger data-testid={`ot-row-approver-${i}`}><SelectValue placeholder="Select approver" /></SelectTrigger>
                      <SelectContent>
                        {approvers.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Input value={r.remarks} onChange={(e) => updateRow(i, { remarks: e.target.value })} placeholder={r.category === "Daily Shifting" ? "Daily shifting remarks" : ""} data-testid={`ot-row-remarks-${i}`} /></TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" onClick={() => removeRow(i)} data-testid={`ot-row-remove-${i}`} className="text-destructive h-8 w-8">
                      <Trash className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={addRow} data-testid="ot-add-row" className="gap-1.5">
            <Plus className="h-4 w-4" /> Add row
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="ot-submit" className="gap-1.5 font-semibold">
            <PaperPlaneTilt className="h-4 w-4" /> {busy ? "Submitting..." : "Submit overtime"}
          </Button>
        </div>
      </Card>

      <div>
        <h2 className="font-heading text-lg font-bold mb-3">My overtime filings</h2>
        <Card className="border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Date filed</TableHead>
                  <TableHead>Cut off</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Total hrs</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mine.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No overtime filed yet.</TableCell></TableRow>
                ) : mine.map((o) => (
                  <TableRow key={o.id} data-testid={`my-ot-${o.id}`}>
                    <TableCell className="font-medium">{o.date_filed}</TableCell>
                    <TableCell className="text-muted-foreground">{o.cut_off || "—"}</TableCell>
                    <TableCell>{o.rows?.length || 0}</TableCell>
                    <TableCell>{o.total_hours}</TableCell>
                    <TableCell><Badge variant="outline" className={`capitalize font-medium ${STATUS_BADGE[o.status]}`}>{o.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
